import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { omitKeys } from '@adsk/alloy-react-helpers';

import ConnectedReferencePicker from './ConnectedReferencePicker';
import { pluginShape, relationshipEntityShape } from './helpers/propTypes';
import ReferencePickerProvider from './providers/ReferencePickerProvider';

/**
 * A ReferencePicker component.
 * This component allows creating references between two entities.
 * When using this component, use targetPlugins to determine which entity types you want to allow linking to.
 * this entity uses Relationship Service as the BE for saving relationships.
 *
 * If you want to add support for a new type, follow: https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/reference-picker/ADD_NEW_PLUGIN.md
 */
const ReferencePicker = React.forwardRef((props, ref) => {
  const {
    sourceEntityId,
    sourceEntityDomain,
    sourceEntityType,
    relationshipEntitiesList,
    onPluginsLoadError,
  } = props;
  const sourceIdAsArr = useMemo(
    () => (sourceEntityId ? [sourceEntityId] : []),
    [sourceEntityId],
  );
  const disableRelationshipService = 'relationshipEntitiesList' in props;

  const { targetPlugins, ...rest } = props;

  return (
    <ReferencePickerProvider
      sourceEntityIds={sourceIdAsArr}
      sourceEntityDomain={sourceEntityDomain}
      sourceEntityType={sourceEntityType}
      plugins={targetPlugins}
      relationshipEntitiesList={relationshipEntitiesList}
      disableRelationshipService={disableRelationshipService}
      onPluginsLoadError={onPluginsLoadError}
    >
      <ConnectedReferencePicker
        {...rest}
        disableRelationshipService={disableRelationshipService}
        ref={ref}
      />
    </ReferencePickerProvider>
  );
});

ReferencePicker.displayName = 'ReferencePicker';

ReferencePicker.propTypes = {
  ...omitKeys(ConnectedReferencePicker.propTypes, [
    'disableRelationshipService',
    'targetPlugins',
  ]),
  /**
   * The plugins we want to allow linking to
   */
  targetPlugins: PropTypes.arrayOf(
    PropTypes.oneOfType([pluginShape, PropTypes.instanceOf(Promise)]),
  ).isRequired,
  /**
   * A callback function that is called when some plugins fail to load.
   */
  onPluginsLoadError: PropTypes.func,
  /**
   * This can be used when we want to use ReferencePicker with custom relationship entities source.
   */
  relationshipEntitiesList: PropTypes.arrayOf(relationshipEntityShape),
};

export default ReferencePicker;
